import { useEffect, useState } from 'react';
import { NavLink } from 'react-router';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
} from '@mui/material';

import { Breakpoints } from '@utils/constants';
import { formatIsoToDate } from '@utils/date';
import type { User } from '@models/user';

import { useUsersQuery } from '@features/users/queries';
import { useMediaQuery } from '@hooks/useMediaQuery';

import LoadingAnimation from '@components/LoadingAnimation';
import s from './UsersPage.module.css';

type UserListProps = {
  users: User[];
  breakPoints: { isMobile: boolean; isCustomBreakpoint: boolean };
};

const UsersList = ({ users, breakPoints }: UserListProps) => {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow className={s.tableHeaderRow}>
            <TableCell>
              Name <i>(A-Z)</i>
            </TableCell>
            <Tooltip title="Displays wether user is activatated or not">
              <TableCell>Status</TableCell>
            </Tooltip>
            {!breakPoints.isCustomBreakpoint && <TableCell>Email</TableCell>}
            {!breakPoints.isMobile && (
              <>
                <TableCell>Last Logged In</TableCell>
                <TableCell>Last Updated</TableCell>
              </>
            )}
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(
            ({ id, name, email, isActive, lastLoggedIn, photoUrl, lastUpdatedProfile }) => (
              <TableRow key={id}>
                <TableCell>
                  <div className={s.usersCell}>
                    <img alt={name} src={photoUrl} className={s.usersImage} />
                    <p className={s.usersName}>{name}</p>
                  </div>
                </TableCell>
                <TableCell>
                  {isActive ? <ToggleOnIcon color="success" /> : <ToggleOffIcon color="error" />}
                </TableCell>
                {!breakPoints.isCustomBreakpoint && <TableCell>{email}</TableCell>}
                {!breakPoints.isMobile && (
                  <>
                    <TableCell>{formatIsoToDate(lastLoggedIn).toUpperCase()}</TableCell>
                    <TableCell>{formatIsoToDate(lastUpdatedProfile).toUpperCase()}</TableCell>
                  </>
                )}
                <TableCell>
                  <NavLink to={`/dashboard/users/${id}/edit`} className={s.editUserLink}>
                    <EditIcon />
                  </NavLink>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

function UsersPage() {
  const { data: users, isLoading } = useUsersQuery();
  const isMobile = useMediaQuery({ query: `(max-width: ${Breakpoints.md})` });
  const isCustomBreakpoint = useMediaQuery({ query: `(max-width: 590px)` });
  const [usersList, setUsersList] = useState<User[]>([]);

  useEffect(() => {
    if (users) setUsersList(users);
  }, [users]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    if (users && usersList) {
      const filteredUsers = users.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm) ||
          user.email.toLowerCase().includes(searchTerm),
      );
      setUsersList(filteredUsers);
    }
  };

  if (isLoading) return <LoadingAnimation />;

  return (
    <div className={s.usersContainer}>
      <div className={s.usersHeaderWrapper}>
        <h1 className={s.usersTitle}>Users</h1>
        <div className={s.userSearchBox}>
          <TextField
            size="small"
            label="Search by name or email"
            variant="outlined"
            className={s.userSearchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      <div className={s.usersTable}>
        {users && <UsersList users={usersList} breakPoints={{ isMobile, isCustomBreakpoint }} />}
      </div>
    </div>
  );
}

export default UsersPage;
